import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mic, Send, Loader2, Sparkles, Clock, Truck, Coffee, Wrench, MapPin, AlertTriangle, CheckCircle2, FileSpreadsheet } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const CATEGORIE_META = {
  preparation:   { label: 'Préparation', icon: Wrench, color: 'bg-slate-100 text-slate-700 border-slate-300' },
  deplacement:   { label: 'Déplacement', icon: Truck,   color: 'bg-blue-100 text-blue-700 border-blue-300' },
  prestation:    { label: 'Prestation',  icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  entretien:     { label: 'Entretien',   icon: Wrench,  color: 'bg-amber-100 text-amber-700 border-amber-300' },
  pause:         { label: 'Pause',       icon: Coffee,  color: 'bg-purple-100 text-purple-700 border-purple-300' },
  interruption:  { label: 'Interruption',icon: AlertTriangle, color: 'bg-orange-100 text-orange-700 border-orange-300' },
  administratif: { label: 'Administratif',icon: FileSpreadsheet, color: 'bg-gray-100 text-gray-700 border-gray-300' },
  autre:         { label: 'Autre',       icon: Clock,   color: 'bg-stone-100 text-stone-700 border-stone-300' },
};

function fmtDuree(min) {
  if (!min) return '0min';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h${m > 0 ? m.toString().padStart(2, '0') : ''}` : `${m}min`;
}

// Composant : formulaire de relance Valentine bidirectionnelle
function RelanceForm({ questions, journeeId, onComplete }) {
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post('/journee-chauffeur/repondre-questions', {
        journee_id: journeeId,
        reponses: answers,
      });
      toast.success('Journée affinée avec tes réponses');
      onComplete?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setSubmitting(false); }
  };
  return (
    <div className="space-y-2">
      {questions.map((q, idx) => (
        <div key={idx}>
          <Label className="text-xs">{q.question}{q.obligatoire && <span className="text-red-500"> *</span>}</Label>
          <Input
            value={answers[q.champ] || ''}
            onChange={(e) => setAnswers({ ...answers, [q.champ]: e.target.value })}
            className="h-8 text-sm"
            data-testid={`relance-input-${q.champ}`}
          />
        </div>
      ))}
      <Button size="sm" onClick={submit} disabled={submitting} data-testid="btn-submit-relance">
        {submitting ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <Sparkles className="w-3 h-3 mr-2" />}
        Affiner avec mes réponses
      </Button>
    </div>
  );
}

export default function JourneeChauffeurPage() {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [recording, setRecording] = useState(false);
  const [result, setResult] = useState(null);

  // Cumul mensuel
  const [cumulMois, setCumulMois] = useState(new Date().toISOString().slice(0, 7));
  const [cumul, setCumul] = useState(null);
  const [loadingCumul, setLoadingCumul] = useState(false);

  const fetchCumul = async () => {
    if (!cumulMois) return;
    setLoadingCumul(true);
    try {
      const [annee, mois] = cumulMois.split('-');
      const r = await api.get('/journee-chauffeur/cumul-mensuel', {
        params: { annee: parseInt(annee), mois: parseInt(mois) }
      });
      setCumul(r.data);
    } catch (e) {
      toast.error('Erreur cumul mensuel');
    } finally { setLoadingCumul(false); }
  };

  useEffect(() => { fetchCumul(); }, [cumulMois]);

  // TTS : lit la réponse de Valentine à voix haute
  const speakResponse = (text) => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'fr-FR';
    u.rate = 1.05;
    window.speechSynthesis.speak(u);
  };

  const handleAnalyser = async () => {
    if (!description.trim()) { toast.error('Décris d\'abord ta journée'); return; }
    setAnalyzing(true);
    try {
      const r = await api.post('/journee-chauffeur/analyser', {
        description, date_journee: date, apply: true,
      });
      setResult(r.data);
      if (r.data.confidence < 40) {
        toast.error('Récit trop vague — Valentine n\'a pas pu structurer. Détaille plus.');
        speakResponse("Récit trop vague. Détaille plus pour que je puisse structurer.");
      } else {
        toast.success(`Journée analysée (${r.data.confidence}% confiance)`);
        // Synthèse vocale courte
        const synth = r.data?.synthese || {};
        const nbActivites = (r.data?.activites || []).length;
        const h = Math.round((synth.total_minutes || 0) / 60);
        const resume = `Journée analysée. ${nbActivites} activités détectées. Total ${h} heures. Confiance ${r.data.confidence}%.`;
        speakResponse(resume);
        fetchCumul();  // refresh
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur analyse IA');
    } finally { setAnalyzing(false); }
  };

  // Saisie vocale Push-to-Talk : maintenir appuyé pour parler, relâcher pour envoyer
  const startVocal = async () => {
    if (!('mediaDevices' in navigator)) {
      toast.error('Microphone indisponible');
      return;
    }
    if (window._currentRecorder) return; // déjà en cours
    try {
      // Couper le TTS en cours pour éviter la boucle "Valentine parle en même temps que moi"
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];
      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks, { type: 'audio/webm' });
        if (blob.size < 500) {
          // Trop court → bruit parasite, on ignore
          setRecording(false);
          window._currentRecorder = null;
          return;
        }
        const fd = new FormData();
        fd.append('file', blob, 'journee.webm');
        toast.info('Transcription en cours…');
        try {
          const r = await api.post('/journee-chauffeur/transcrire', fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          if (r.data.text) {
            setDescription(prev => (prev ? prev + ' ' : '') + r.data.text);
            toast.success('Transcription OK');
          }
        } catch (err) {
          toast.error('Transcription impossible. Tape le texte directement.');
        }
        setRecording(false);
        window._currentRecorder = null;
      };
      recorder.start();
      window._currentRecorder = recorder;
      setRecording(true);
    } catch (e) {
      toast.error('Permission micro refusée');
    }
  };

  const stopVocal = () => {
    if (window._currentRecorder && window._currentRecorder.state === 'recording') {
      window._currentRecorder.stop();
    }
  };

  const handleExportPaye = async () => {
    const email = window.prompt('Email du service paye :', 'paye@etasauvayre.com');
    if (!email) return;
    try {
      const [annee, mois] = cumulMois.split('-');
      const fd = new FormData();
      fd.append('annee', annee);
      fd.append('mois', mois);
      fd.append('email_paye', email);
      const r = await api.post('/journee-chauffeur/export-paye', fd);
      if (r.data.success) toast.success(`Email envoyé à ${email} (${r.data.chauffeurs_count} salariés)`);
      else toast.error('Email non envoyé');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur export paye');
    }
  };

  return (
    <div className="space-y-6 pb-20" data-testid="journee-chauffeur-page">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Sparkles className="w-7 h-7 text-emerald-600" />
          Journée chauffeur — Valentine
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Décris ta journée, l'IA structure tes activités, calcule les heures par client et le total à facturer.</p>
      </div>

      {/* Saisie */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="space-y-1">
              <Label>Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" />
            </div>
            <div className="ml-auto flex gap-2 items-center">
              <span className="hidden sm:inline text-xs text-muted-foreground">Maintiens pour parler</span>
              <Button
                variant={recording ? 'destructive' : 'outline'}
                className={recording ? 'animate-pulse bg-red-600 hover:bg-red-700 text-white' : ''}
                onMouseDown={startVocal}
                onMouseUp={stopVocal}
                onMouseLeave={() => { if (recording) stopVocal(); }}
                onTouchStart={(e) => { e.preventDefault(); startVocal(); }}
                onTouchEnd={(e) => { e.preventDefault(); stopVocal(); }}
                onContextMenu={(e) => e.preventDefault()}
                style={{ touchAction: 'none', userSelect: 'none' }}
                data-testid="btn-vocal"
              >
                <Mic className="w-4 h-4 mr-2" /> {recording ? 'Parle…' : 'Push-to-talk'}
              </Button>
            </div>
          </div>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={`Exemple :\n8h arrivée dépôt. J'ai pris le John Deere 6155R + cover-crop. 1h de route jusqu'au Domaine du Gourget. 2h de labour parcelle Truffier. Pause midi 30min. Retour au dépôt 1h. Changement tracteur Renault + broyeur. Aller chez Client X 1h. 2h broyage là-bas (15min interruption tél à facturer quand même). Retour 1h.`}
            rows={8}
            className="font-mono text-sm"
            data-testid="journee-textarea"
          />
          <div className="flex justify-end">
            <Button onClick={handleAnalyser} disabled={analyzing || !description.trim()}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-analyser">
              {analyzing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Valentine analyse…</> : <><Sparkles className="w-4 h-4 mr-2" /> Analyser ma journée</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Résultat */}
      {result && (
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Activités détectées</h3>
              <span className="text-xs text-muted-foreground">Confiance {result.confidence}%</span>
            </div>

            {/* Synthèse */}
            {result.synthese && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3">
                <div><Label className="text-xs">Total journée</Label><div className="font-bold text-lg">{fmtDuree(result.synthese.duree_totale_minutes)}</div></div>
                <div><Label className="text-xs">Prestation</Label><div className="font-bold text-emerald-600">{fmtDuree(result.synthese.duree_prestation_minutes)}</div></div>
                <div><Label className="text-xs">Déplacement</Label><div className="font-bold text-blue-600">{fmtDuree(result.synthese.duree_deplacement_minutes)}</div></div>
                <div><Label className="text-xs">Pause</Label><div className="font-bold text-purple-600">{fmtDuree(result.synthese.duree_pause_minutes)}</div></div>
              </div>
            )}

            {/* Activités */}
            <div className="space-y-2">
              {(result.activites || []).map((a, idx) => {
                const meta = CATEGORIE_META[a.categorie] || CATEGORIE_META.autre;
                const Icon = meta.icon;
                return (
                  <div key={idx} className={`flex items-start gap-3 p-3 rounded-lg border ${meta.color}`} data-testid={`activite-${idx}`}>
                    <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-sm">{a.description}</span>
                        {a.facturable && <span className="text-[10px] px-1.5 py-0.5 bg-emerald-600 text-white rounded">€ Facturable</span>}
                        {a.facturable && a.tva_suggere != null && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-300 rounded inline-flex items-center gap-1" title={a.tva_article_loi || 'TVA suggérée par Valentine'}>
                            <Sparkles className="w-2.5 h-2.5" />
                            TVA {a.tva_suggere}%{a.tva_article_loi ? ` · ${a.tva_article_loi}` : ''}
                          </span>
                        )}
                      </div>
                      <div className="text-xs flex flex-wrap gap-3 mt-1">
                        {a.client_nom && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {a.client_nom}{a.lieu ? ` · ${a.lieu}` : ''}</span>}
                        {a.materiel?.length > 0 && <span>🚜 {a.materiel.join(', ')}</span>}
                        <span className="font-mono ml-auto">{fmtDuree(a.duree_minutes)}</span>
                      </div>
                      {a.notes && <div className="text-xs italic mt-1 opacity-80">{a.notes}</div>}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Clients à facturer */}
            {result.synthese?.clients_factures?.length > 0 && (
              <div className="border-t pt-3">
                <h4 className="font-semibold text-sm mb-2">💰 À facturer</h4>
                <div className="space-y-1">
                  {result.synthese.clients_factures.map((c, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm bg-emerald-50 dark:bg-emerald-950/30 rounded px-3 py-2">
                      <span className="font-medium">{c.nom}</span>
                      <span className="font-mono text-emerald-700 dark:text-emerald-400 font-bold">{fmtDuree(c.duree_minutes)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.synthese?.anomalies?.length > 0 && (
              <div className="border-t pt-3 text-sm bg-amber-50 dark:bg-amber-950/30 rounded p-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <strong>Anomalies :</strong>
                  <ul className="list-disc list-inside text-xs mt-1">
                    {result.synthese.anomalies.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              </div>
            )}

            {/* Phase 2 : Consommables auto-déduits */}
            {(result.consommables_appliques || []).length > 0 && (
              <div className="border-t pt-3">
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">⛽ Consommables déduits du stock atelier</h4>
                <div className="space-y-1">
                  {result.consommables_appliques.map((c, idx) => (
                    <div key={idx} className={`text-sm rounded px-3 py-2 flex items-center justify-between ${c.success ? 'bg-blue-50 dark:bg-blue-950/30' : 'bg-red-50 dark:bg-red-950/30'}`}>
                      <span>
                        {c.success ? '✅' : '❌'} {c.demande?.nom} — <strong>{c.demande?.quantite} {c.demande?.unite}</strong>
                        {c.success && c.en_alerte && <span className="ml-2 text-red-500">⚠️ Stock bas</span>}
                      </span>
                      <span className="text-xs">
                        {c.success ? `Stock: ${c.nouveau_stock} ${c.unite}` : c.error}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Phase 4 : Matching GPS ↔ clients */}
            {(result.gps_matches || []).length > 0 && (
              <div className="border-t pt-3">
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">📍 Détection GPS — Présences chez clients</h4>
                <div className="space-y-1 text-sm">
                  {result.gps_matches.map((m, idx) => (
                    <div key={idx} className="bg-indigo-50 dark:bg-indigo-950/30 rounded px-3 py-2">
                      <div className="font-medium">{m.client_nom}</div>
                      <div className="text-xs text-muted-foreground">{m.adresse} — {m.nb_pings_proches} positions GPS détectées entre {m.premier_ping?.slice(11, 16)} et {m.dernier_ping?.slice(11, 16)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Phase 4 : Questions de relance Valentine */}
            {(result.questions_relance || []).length > 0 && result.journee_id && (
              <div className="border-t pt-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg p-3 -mx-3" data-testid="questions-relance-section">
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  Valentine te demande des précisions :
                </h4>
                <RelanceForm
                  questions={result.questions_relance}
                  journeeId={result.journee_id}
                  onComplete={(updated) => { setResult({ ...result, ...updated, journee_id: result.journee_id }); fetchCumul(); }}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Cumul mensuel */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2"><FileSpreadsheet className="w-5 h-5" /> Cumul mensuel</h3>
            <Input type="month" value={cumulMois} onChange={(e) => setCumulMois(e.target.value)} className="w-44" />
          </div>

          {loadingCumul ? <div className="text-sm text-muted-foreground">Chargement…</div> : cumul && (
            <>
              {(cumul.chauffeurs || []).length === 0 ? (
                <p className="text-sm text-muted-foreground italic">Aucune journée enregistrée pour ce mois.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-muted-foreground border-b">
                      <tr>
                        <th className="text-left py-2">Chauffeur</th>
                        <th className="text-right py-2">Jours</th>
                        <th className="text-right py-2">Réalisées</th>
                        <th className="text-right py-2">Contractuelles</th>
                        <th className="text-right py-2 text-emerald-700">À payer</th>
                        <th className="text-right py-2 text-blue-700">Récup (annualisé)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cumul.chauffeurs.map((c) => (
                        <tr key={c.chauffeur_id} className="border-b hover:bg-slate-50 dark:hover:bg-slate-900/30" data-testid={`cumul-row-${c.chauffeur_id}`}>
                          <td className="py-2 font-medium">{c.chauffeur_nom || c.chauffeur_id}</td>
                          <td className="text-right py-2">{c.jours_travailles}</td>
                          <td className="text-right py-2 font-mono">{c.heures_realisees}h</td>
                          <td className="text-right py-2 font-mono text-muted-foreground">{c.heures_contractuelles}h</td>
                          <td className="text-right py-2 font-mono font-bold text-emerald-700">{c.heures_a_payer}h</td>
                          <td className="text-right py-2 font-mono text-blue-700">{c.heures_recup}h</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex justify-end pt-2">
                <Button onClick={handleExportPaye} disabled={!cumul.chauffeurs?.length}
                  className="bg-blue-600 hover:bg-blue-700" data-testid="btn-export-paye">
                  <Send className="w-4 h-4 mr-2" /> Envoyer au service paye
                </Button>
              </div>

              <p className="text-xs text-muted-foreground italic">
                Les heures de récupération (annualisation) ne sont PAS envoyées au service paye — elles restent comptées en interne pour les jours de récup ultérieurs.
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
